import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { axiosTokenInstance } from '../../utils/axiosInstance.utils';
import { apiUrls, routeURL } from '../../utils/properties.utils';

type DownloadError = {
  message?: string;
  response?: {
    data?: Blob | { message?: string; error?: string };
  };
};

const getDownloadErrorMessage = async (error: unknown) => {
  const downloadError = error as DownloadError;
  const data = downloadError.response?.data;

  if (data instanceof Blob) {
    const text = await data.text();

    try {
      const parsed = JSON.parse(text);
      return parsed?.message || parsed?.error || text;
    } catch {
      return text;
    }
  }

  return (
    data?.message ||
    data?.error ||
    downloadError.message ||
    'Failed to download invoice.'
  );
};

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isDownloading, setIsDownloading] = useState(false);

  const orderId = searchParams.get('order_id');

  const downloadInvoice = async () => {
    const response = await axiosTokenInstance.get(
      apiUrls.DOWNLOAD_INVOICE_BY_ORDER(orderId as string),
      { responseType: 'blob' }
    );

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');

    link.href = url;
    link.download = `invoice-${orderId}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();

    window.URL.revokeObjectURL(url);
  };

  const handleDownloadInvoice = async () => {
    if (!orderId || isDownloading) return;

    try {
      setIsDownloading(true);
      await downloadInvoice();
    } catch (error) {
      console.error('Download invoice failed:', error);
      alert(await getDownloadErrorMessage(error));
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Box sx={{ p: 5 }}>
      <Typography variant="h4" color="success.main">
        Payment Successful
      </Typography>

      <Typography sx={{ mt: 2 }}>
        Your subscription payment was completed successfully.
      </Typography>

      <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          onClick={() =>
            navigate(routeURL.USER + routeURL.SETTINGS, {
              state: { selectedTab: 2 },
            })
          }
        >
          Back to Billing
        </Button>

        <Button
          variant="outlined"
          disabled={!orderId || isDownloading}
          onClick={handleDownloadInvoice}
          startIcon={isDownloading ? <CircularProgress size={16} /> : null}
        >
          {isDownloading ? 'Downloading...' : 'Download Invoice'}
        </Button>
      </Box>
    </Box>
  );
}
